<template>
  <div class="refund-page">
    <van-nav-bar fixed title="申请退款" left-text="返回" left-arrow @click-left="goBack" />

    <template v-if="order">
      <div class="refund-body">
        <!-- 订单信息 -->
        <div class="order-card">
          <div class="oc-head">
            <span class="oc-no">订单号：{{ order.order_no }}</span>
            <span class="oc-status">{{ order.state_text }}</span>
          </div>
          <div class="goods-list">
            <div class="goods-item" v-for="g in order.goods" :key="g.goods_id">
              <img :src="g.goods_image" alt="" />
              <div class="gi-info">
                <div class="gi-name text-ellipsis-2">{{ g.goods_name }}</div>
                <div class="gi-spec" v-if="g.spec_text">{{ g.spec_text }}</div>
                <div class="gi-price">¥{{ g.goods_price }} × {{ g.total_num }}</div>
              </div>
            </div>
          </div>
          <div class="oc-footer">
            共{{ order.goods_total_num }}件商品，退款金额：<b>¥{{ order.total_price }}</b>
          </div>
        </div>

        <!-- 退款金额 -->
        <div class="reason-card">
          <div class="rc-label">退款金额</div>
          <div class="amount-row">
            <span class="amount-symbol">¥</span>
            <input class="amount-input" type="number" v-model.trim="amount" min="0.01" :max="order.total_price" step="0.01" placeholder="请输入退款金额" />
          </div>
          <div class="rc-tip">可退上限 ¥{{ order.total_price }}，默认全额退款；审核通过后按原支付渠道退回</div>
        </div>

        <!-- 退款原因 -->
        <div class="reason-card">
          <div class="rc-label">退款原因</div>
          <textarea
            class="reason-input"
            v-model.trim="reason"
            rows="4"
            maxlength="500"
            placeholder="请填写退款原因（至少 5 个字）"
          ></textarea>
          <div class="rc-count">{{ reason.length }}/500</div>
        </div>

        <!-- 温馨提示 - 居中卡片（修复版） -->
        <div class="tip-card">
          <van-icon name="info-o" size="18" color="#ff8c42" class="tip-icon" />
          <span class="tip-text">{{ tipText }}</span>
        </div>

        <button class="submit-btn" :disabled="submitting" @click="submit">提交申请</button>
      </div>
    </template>

    <!-- 无订单数据 -->
    <div v-else class="empty-state">
      <van-icon name="orders-o" size="60" color="#ccc" />
      <p>未找到需要退款的订单</p>
      <van-button type="primary" round @click="$router.replace('/myorder')">去我的订单</van-button>
    </div>

    <div class="bottom-placeholder"></div>
  </div>
</template>

<script>
import { applyRefundApi } from '@/api/order'
import { Toast } from 'vant'
import { readOrder } from '@/utils/orderTransfer'
import { saveOrderTab } from '@/utils/orderTab'

export default {
  name: 'Refund',
  data () {
    return {
      order: null,
      reason: '',
      amount: '',
      submitting: false
    }
  },
  computed: {
    // 拼单「未成团」订单（待成团 / 拼团失败）：提交后由系统直接同意退款，无需人工处理
    isUnformedGroup () {
      if (!this.order) return false
      const isGroup = Number(this.order.is_group) === 1 || Number(this.order.group_status) > 0
      const gs = Number(this.order.group_status) || 0
      return isGroup && (gs === 1 || gs === 3)
    },
    tipText () {
      if (this.isUnformedGroup) {
        return '该订单为未成团的拼单订单，提交申请后系统将自动同意退款，款项按原支付渠道原路退回，无需等待客服人工处理。'
      }
      return '提交申请后订单将进入退款处理中，客服会在 1~3 个工作日内处理，审核通过后款项将原路退回至您的支付账户。'
    }
  },
  created () {
    this.order = readOrder()
    // 默认全额退款
    if (this.order && this.order.total_price) {
      this.amount = String(this.order.total_price)
    }
  },
  methods: {
    goBack () {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        this.$router.replace('/myorder')
      }
    },
    async submit () {
      if (!this.order) return
      if (this.reason.length < 5) {
        Toast('请填写退款原因（至少 5 个字）')
        return
      }
      const total = Number(this.order.total_price) || 0
      const amt = Number(this.amount)
      if (!amt || amt <= 0) {
        Toast('请输入正确的退款金额')
        return
      }
      if (amt > total + 0.001) {
        Toast('退款金额不能超过订单总额 ¥' + total.toFixed(2))
        return
      }
      this.submitting = true
      try {
        const res = await applyRefundApi(this.order.order_id, this.reason, amt)
        // 未成团拼单订单：后端已自动同意并原路退款
        if (res && res.data && res.data.auto_refund) {
          Toast.success(res.message || '拼团未成团，已自动退款')
        } else {
          Toast.success('退款申请已提交')
        }
        saveOrderTab('refund')
        this.$router.replace('/myorder')
      } catch (err) {
        Toast(typeof err === 'string' ? err : '提交失败，请重试')
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style lang="less" scoped>
.refund-page {
  min-height: 100vh;
  background: #fafafa;
  padding-top: 46px;

  /deep/ .van-nav-bar__arrow { color: #333; }
  /deep/ .van-nav-bar__text { color: #333; }
  /deep/ .van-nav-bar { z-index: 100; }

  .refund-body {
    padding: 12px;

    .order-card {
      background: #fff;
      border-radius: 8px;
      padding: 14px;
      box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);

      .oc-head {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 10px;

        .oc-no {
          font-size: 13px;
          font-weight: 600;
          color: #333;
        }

        .oc-status {
          font-size: 12px;
          color: #ff6b35;
        }
      }

      .goods-list {
        border-top: 1px solid #f5f5f5;
        padding-top: 10px;

        .goods-item {
          display: flex;
          margin-bottom: 10px;

          img {
            width: 56px;
            height: 56px;
            border-radius: 6px;
            object-fit: cover;
            flex-shrink: 0;
            margin-right: 10px;
          }

          .gi-info {
            flex: 1;
            min-width: 0;

            .gi-name {
              font-size: 13px;
              color: #333;
              line-height: 18px;
            }

            .gi-spec {
              display: inline-block;
              margin-top: 4px;
              padding: 1px 6px;
              background: #f7f8fa;
              border-radius: 4px;
              font-size: 12px;
              color: #999;
            }

            .gi-price {
              font-size: 12px;
              color: #999;
              margin-top: 4px;
            }
          }
        }
      }

      .oc-footer {
        text-align: right;
        font-size: 13px;
        color: #333;
        border-top: 1px solid #f5f5f5;
        padding-top: 10px;

        b {
          color: #ff6b35;
          font-size: 16px;
        }
      }
    }

    .reason-card {
      background: #fff;
      border-radius: 8px;
      padding: 14px;
      margin-top: 12px;
      box-shadow: 0 0.5px 2px 0 rgba(0, 0, 0, 0.05);

      .rc-label {
        font-size: 14px;
        font-weight: 600;
        color: #333;
        margin-bottom: 8px;
      }

      .amount-row {
        display: flex;
        align-items: center;
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        padding: 4px 10px;
        background: #fafafa;

        .amount-symbol {
          font-size: 18px;
          color: #ff6b35;
          font-weight: 700;
          margin-right: 6px;
        }

        .amount-input {
          flex: 1;
          border: none;
          outline: none;
          background: transparent;
          font-size: 20px;
          font-weight: 700;
          color: #ff6b35;
          padding: 4px 0;
          width: 100%;
        }
      }

      .rc-tip {
        font-size: 12px;
        color: #999;
        margin-top: 6px;
      }

      .reason-input {
        width: 100%;
        box-sizing: border-box;
        border: 1px solid #e5e5e5;
        border-radius: 8px;
        padding: 10px;
        font-size: 14px;
        font-family: inherit;
        resize: none;
        background: #fafafa;
        outline: none;

        &:focus {
          border-color: #ff8c42;
        }
      }

      .rc-count {
        text-align: right;
        font-size: 12px;
        color: #bbb;
        margin-top: 4px;
      }
    }

    // 温馨提示卡片 - 强制居中
    .tip-card {
      display: flex;
      align-items: center;
      justify-content: center;
      background: #fff8f0;
      border-radius: 8px;
      padding: 12px 16px;
      margin: 12px 0;
      border: 1px solid #ffefe5;
      text-align: center; // 关键：文字居中

      .tip-icon {
        flex-shrink: 0;
        margin-right: 6px;
      }

      .tip-text {
        font-size: 13px;
        color: #666;
        line-height: 1.6;
        text-align: center; // 关键：文字居中
      }
    }

    .submit-btn {
      width: 100%;
      height: 44px;
      border: none;
      border-radius: 22px;
      background: linear-gradient(90deg, #ff6b35, #ff8c42);
      color: #fff;
      font-size: 16px;
      cursor: pointer;

      &:disabled {
        opacity: 0.6;
      }
    }
  }

  .empty-state {
    text-align: center;
    padding: 120px 20px;

    p {
      color: #999;
      margin: 12px 0 20px;
    }
  }

  .bottom-placeholder {
    height: 20px;
  }
}
</style>